import { onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { handleOctadeskViewportChange } from './useOctadeskDomLayout'

export const FLOATING_CHAT_ACTIONS_BOTTOM_CSS_VAR = '--floating-chat-actions-bottom'
export const FLOATING_CHAT_BOTTOM_WITHOUT_FOOTER = '1.5rem'
export const FLOATING_CHAT_FOOTER_GAP_PX = 16
export const MIN_VISIBLE_PAGE_FOOTER_HEIGHT_PX = 48

const PAGE_FOOTER_CONTAINER_SELECTORS = [
  '.base-index-page-footer',
  '.base-index-content-footer',
  '.layout-main [class*="bottom-0"]'
] as const

function hasStickyBottomClass (className: string): boolean {
  return className.includes('bottom-0') && className.includes('sticky')
}

function isPageFooterElement (element: HTMLElement): boolean {
  if (
    element.classList.contains('base-index-page-footer') ||
    element.classList.contains('base-index-content-footer')
  ) {
    return true
  }

  const className = element.className
  if (typeof className !== 'string' || !hasStickyBottomClass(className)) {
    return false
  }

  return element.closest('.layout-main') !== null
}

function isVisibleElement (element: HTMLElement): boolean {
  const style = getComputedStyle(element)
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false
  }

  const rect = element.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

function isFooterAnchoredToViewportBottom (element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()
  if (rect.height < MIN_VISIBLE_PAGE_FOOTER_HEIGHT_PX) {
    return false
  }

  return rect.bottom >= window.innerHeight - 2 && rect.top < window.innerHeight
}

export function findActivePageFooter (): HTMLElement | null {
  const candidates = new Set<HTMLElement>()

  for (const selector of PAGE_FOOTER_CONTAINER_SELECTORS) {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      if (isPageFooterElement(element)) {
        candidates.add(element)
      }
    })
  }

  let activeFooter: HTMLElement | null = null
  let highestTop = -1

  candidates.forEach((element) => {
    if (!isVisibleElement(element) || !isFooterAnchoredToViewportBottom(element)) {
      return
    }

    const { top } = element.getBoundingClientRect()
    if (top > highestTop) {
      highestTop = top
      activeFooter = element
    }
  })

  return activeFooter
}

export function resolveFloatingChatActionsBottom (): string {
  const footer = findActivePageFooter()
  if (!footer) {
    return FLOATING_CHAT_BOTTOM_WITHOUT_FOOTER
  }

  const { top } = footer.getBoundingClientRect()
  const bottomPx = Math.max(
    0,
    Math.round(window.innerHeight - top + FLOATING_CHAT_FOOTER_GAP_PX)
  )

  return `${bottomPx}px`
}

export function applyFloatingChatActionsBottom (): void {
  document.documentElement.style.setProperty(
    FLOATING_CHAT_ACTIONS_BOTTOM_CSS_VAR,
    resolveFloatingChatActionsBottom()
  )
  handleOctadeskViewportChange()
}

export function useFloatingChatActionsLayout (): void {
  const route = useRoute()
  let mutationObserver: MutationObserver | null = null
  let footerResizeObserver: ResizeObserver | null = null
  let observedFooter: HTMLElement | null = null
  let syncFrameId: number | null = null

  const unobserveFooter = (): void => {
    if (observedFooter && footerResizeObserver) {
      footerResizeObserver.unobserve(observedFooter)
    }
    observedFooter = null
  }

  const observeFooter = (footer: HTMLElement | null): void => {
    if (footer === observedFooter) {
      return
    }

    unobserveFooter()

    if (!footer) {
      return
    }

    if (!footerResizeObserver) {
      footerResizeObserver = new ResizeObserver(() => {
        scheduleApply()
      })
    }

    footerResizeObserver.observe(footer)
    observedFooter = footer
  }

  const scheduleApply = (): void => {
    if (syncFrameId !== null) {
      cancelAnimationFrame(syncFrameId)
    }

    syncFrameId = requestAnimationFrame(() => {
      syncFrameId = null
      applyFloatingChatActionsBottom()
      observeFooter(findActivePageFooter())
    })
  }

  onMounted(() => {
    scheduleApply()
    window.addEventListener('resize', scheduleApply)
    window.addEventListener('scroll', scheduleApply, true)

    mutationObserver = new MutationObserver(scheduleApply)
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    })
  })

  onUnmounted(() => {
    window.removeEventListener('resize', scheduleApply)
    window.removeEventListener('scroll', scheduleApply, true)
    mutationObserver?.disconnect()
    mutationObserver = null
    footerResizeObserver?.disconnect()
    footerResizeObserver = null
    unobserveFooter()

    if (syncFrameId !== null) {
      cancelAnimationFrame(syncFrameId)
      syncFrameId = null
    }

    document.documentElement.style.removeProperty(FLOATING_CHAT_ACTIONS_BOTTOM_CSS_VAR)
  })

  watch(() => route.fullPath, scheduleApply)
}
